import { useEffect, type ReactNode } from 'react';
import { Box, ButtonBase, Chip, Divider, Stack, Typography } from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { navigate, paths, type Route } from '../../lib/router';
import { useDepartmentStore } from '../../store/departmentStore';
import { PortalHeader } from './PortalHeader';
import { AppFooter } from '../AppFooter';

function NavItem({
  icon,
  label,
  active,
  count,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  count?: number;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        gap: 1.25,
        px: 1.25,
        py: 0.875,
        borderRadius: 2,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        color: active ? 'primary.main' : 'neutral.main',
        bgcolor: active ? 'chrome.tint' : 'transparent',
        '&:hover': { bgcolor: active ? 'chrome.tint' : 'chrome.subtle' },
      }}
    >
      <Box sx={{ display: 'flex', color: active ? 'primary.main' : 'text.secondary', '& svg': { fontSize: 18 } }}>{icon}</Box>
      <Box component="span" sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </Box>
      {count !== undefined && count > 0 && (
        <Typography variant="caption" component="span" sx={{ color: 'text.secondary' }}>
          {count}
        </Typography>
      )}
    </ButtonBase>
  );
}

/** Portal shell: header, left navigation (sections + department folders) and page content. */
export function PortalLayout({ route, children }: { route: Route; children: ReactNode }) {
  const departments = useDepartmentStore((s) => s.departments);
  const refresh = useDepartmentStore((s) => s.refresh);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <PortalHeader search={route.name === 'search' ? route.q : ''} />
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          component="nav"
          aria-label="Portal"
          sx={{
            width: 248,
            flexShrink: 0,
            display: { xs: 'none', md: 'block' },
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            p: 1.5,
          }}
        >
          <Stack spacing={0.25}>
            <NavItem icon={<HomeOutlinedIcon />} label="Home" active={route.name === 'home'} onClick={() => navigate(paths.home())} />
            <NavItem
              icon={<CampaignOutlinedIcon />}
              label="Announcements"
              active={route.name === 'announcements'}
              onClick={() => navigate(paths.announcements())}
            />
            <NavItem
              icon={<PermMediaOutlinedIcon />}
              label="Company assets"
              active={route.name === 'assets'}
              onClick={() => navigate(paths.assets())}
            />
            <NavItem
              icon={<DescriptionOutlinedIcon />}
              label="Documents"
              active={route.name === 'documents'}
              onClick={() => navigate(paths.documents())}
            />
            <NavItem
              icon={<SlideshowOutlinedIcon />}
              label="My decks"
              active={route.name === 'mine'}
              onClick={() => navigate(paths.mine())}
            />
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="overline" component="h2" sx={{ px: 1.25, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Departments
          </Typography>
          <Stack spacing={0.25}>
            {departments.map((d) => (
              <NavItem
                key={d.id}
                icon={<FolderOutlinedIcon sx={{ color: d.color }} />}
                label={d.name}
                count={d.deck_count}
                active={route.name === 'dept' && route.id === d.id}
                onClick={() => navigate(paths.dept(d.id))}
              />
            ))}
          </Stack>
        </Box>
        <Box component="main" sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {/* Small screens: the sidebar is hidden, so offer the same links as a scrolling row. */}
          <Stack
            direction="row"
            spacing={1}
            aria-label="Portal sections"
            sx={{ display: { xs: 'flex', md: 'none' }, overflowX: 'auto', px: 2, pt: 2 }}
          >
            {[
              { label: 'Home', to: paths.home(), on: route.name === 'home' },
              { label: 'Announcements', to: paths.announcements(), on: route.name === 'announcements' },
              { label: 'Company assets', to: paths.assets(), on: route.name === 'assets' },
              { label: 'Documents', to: paths.documents(), on: route.name === 'documents' },
              { label: 'My decks', to: paths.mine(), on: route.name === 'mine' },
              ...departments.map((d) => ({ label: d.name, to: paths.dept(d.id), on: route.name === 'dept' && route.id === d.id })),
            ].map((l) => (
              <Chip
                key={l.to}
                label={l.label}
                onClick={() => navigate(l.to)}
                color={l.on ? 'primary' : 'default'}
                variant={l.on ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Stack>
          <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
            {children}
            <AppFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
